import { supabase } from './supabase'

// Types for messaging
export interface MessageThread {
  id: string
  type: 'press_gaggle' | 'group_thread'
  title: string
  description?: string
  created_by?: string
  created_at: string
  updated_at: string
  is_active: boolean
}

export interface Message {
  id: string
  thread_id: string
  sender_id: string
  content: string
  is_urgent: boolean
  created_at: string
  updated_at: string
  // Joined data from relationships
  sender?: {
    id: string
    name: string
    role: string
    outlet?: string
  }
}

export interface MessageParticipant {
  id: string
  thread_id: string
  user_id: string
  joined_at: string
  last_read_at: string
  is_active: boolean
}

export interface UnreadStatus {
  [threadId: string]: boolean
}

// Messaging Service Class
export class MessagingService {
  
  // Create a new group thread (PR team only)
  async createGroupThread(title: string, description: string, createdBy: number | string, journalistIds: (number | string)[]): Promise<MessageThread | null> {
    // Create the thread
    const { data: thread, error: threadError } = await supabase
      .from('message_threads')
      .insert({
        type: 'group_thread',
        title,
        description,
        created_by: createdBy,
        is_active: true
      })
      .select('*')
      .single()
    
    if (threadError || !thread) {
      console.error('Error creating group thread:', threadError)
      return null
    }

    // Get all PR team members
    const { data: prMembers, error: prError } = await supabase
      .from('users')
      .select('id')
      .in('role', ['pr', 'admin'])
    
    if (prError) {
      console.error('Error fetching PR team members:', prError)
      return null
    }

    // Add all PR team members and selected journalists as participants
    const allParticipantIds = [
      ...(prMembers?.map(u => u.id) || []),
      ...journalistIds
    ]

    const participants = allParticipantIds.map(userId => ({
      thread_id: thread.id,
      user_id: userId,
      is_active: true
    }))

    const { error: participantError } = await supabase
      .from('message_participants')
      .insert(participants)
    
    if (participantError) {
      console.error('Error adding participants:', participantError)
      return null
    }

    return thread
  }

  // Get all group threads for a user
  async getGroupThreadsForUser(userId: number | string): Promise<MessageThread[]> {
    const { data, error } = await supabase
      .from('message_threads')
      .select(`
        *,
        message_participants!inner (user_id)
      `)
      .eq('type', 'group_thread')
      .eq('message_participants.user_id', userId)
      .eq('message_participants.is_active', true)
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching group threads:', error)
      return []
    }
    
    return data || []
  }

  // Delete a group thread (PR team only)
  async deleteGroupThread(threadId: string, userId: number | string): Promise<boolean> {
    try {
      // Verify user is PR team member
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single()
      
      if (userError || !user || !['pr', 'admin'].includes(user.role)) {
        console.error('Unauthorized: Only PR team can delete group threads')
        return false
      }

      // Soft delete: mark thread as inactive
      const { error: threadError } = await supabase
        .from('message_threads')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', threadId)
        .eq('type', 'group_thread') // Only allow deleting group threads, not Press Gaggle
      
      if (threadError) {
        console.error('Error deleting group thread:', threadError)
        return false
      }

      // Mark all participants as inactive
      const { error: participantError } = await supabase
        .from('message_participants')
        .update({ is_active: false })
        .eq('thread_id', threadId)
      
      if (participantError) {
        console.error('Error deactivating participants:', participantError)
        return false
      }

      return true
    } catch (error) {
      console.error('Error deleting group thread:', error)
      return false
    }
  }

  // Search journalists for group thread creation
  async searchJournalists(query: string): Promise<Array<{id: string, name: string, outlet: string, email: string}>> {
    if (!query.trim()) return []
    
    const { data, error } = await supabase
      .from('users')
      .select('id, name, outlet, email')
      .eq('role', 'journalist')
      .or(`name.ilike.%${query}%, outlet.ilike.%${query}%, email.ilike.%${query}%`)
      .limit(10)
    
    if (error) {
      console.error('Error searching journalists:', error)
      return []
    }
    
    return data || []
  }

  // Get current participants of a thread
  async getThreadParticipants(threadId: string): Promise<Array<{id: string, name: string, outlet: string, email: string, role: string}>> {
    const { data, error } = await supabase
      .from('message_participants')
      .select(`
        user_id,
        users!inner (
          id,
          name,
          outlet,
          email,
          role
        )
      `)
      .eq('thread_id', threadId)
      .eq('is_active', true)
    
    if (error) {
      console.error('Error fetching thread participants:', error)
      return []
    }
    
    // Flatten the response to return user objects
    return data?.map(p => p.users as any) || []
  }

  // Add participants to an existing thread (PR team only)
  async addParticipantsToThread(threadId: string, userIds: string[], addedBy: string | number): Promise<boolean> {
    try {
      // Verify user is PR team member
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', addedBy)
        .single()
      
      if (userError || !user || !['pr', 'admin'].includes(user.role)) {
        console.error('Unauthorized: Only PR team can add participants')
        return false
      }

      // Check which users are already participants
      const { data: existingParticipants, error: checkError } = await supabase
        .from('message_participants')
        .select('user_id')
        .eq('thread_id', threadId)
        .in('user_id', userIds)
      
      if (checkError) {
        console.error('Error checking existing participants:', checkError)
        return false
      }

      const existingUserIds = new Set(existingParticipants?.map(p => p.user_id) || [])
      const newUserIds = userIds.filter(id => !existingUserIds.has(id))
      
      if (newUserIds.length === 0) {
        console.log('All users are already participants')
        return true
      }

      // Add new participants
      const newParticipants = newUserIds.map(userId => ({
        thread_id: threadId,
        user_id: userId,
        is_active: true
      }))

      const { error: insertError } = await supabase
        .from('message_participants')
        .insert(newParticipants)
      
      if (insertError) {
        console.error('Error adding participants:', insertError)
        return false
      }

      // Update thread's updated_at timestamp
      const { error: updateError } = await supabase
        .from('message_threads')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', threadId)
      
      if (updateError) {
        console.error('Error updating thread timestamp:', updateError)
      }

      return true
    } catch (error) {
      console.error('Error adding participants to thread:', error)
      return false
    }
  }

  // Remove a participant from a thread (PR team only)
  async removeParticipantFromThread(threadId: string, userId: string, removedBy: string | number): Promise<boolean> {
    try {
      // Verify user is PR team member
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', removedBy)
        .single()
      
      if (userError || !user || !['pr', 'admin'].includes(user.role)) {
        console.error('Unauthorized: Only PR team can remove participants')
        return false
      }

      // Soft delete: mark participant as inactive
      const { error } = await supabase
        .from('message_participants')
        .update({ is_active: false })
        .eq('thread_id', threadId)
        .eq('user_id', userId)
      
      if (error) {
        console.error('Error removing participant:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error removing participant from thread:', error)
      return false
    }
  }

  // Clear Press Gaggle (Admin only)
  async clearPressGaggle(): Promise<boolean> {
    try {
      console.log('messagingService.clearPressGaggle: Starting press gaggle clear')
      
      // Get the Press Gaggle thread
      const thread = await this.getPressGaggleThread()
      if (!thread) {
        console.log('messagingService.clearPressGaggle: No press gaggle thread found')
        return true // Nothing to clear
      }

      console.log('messagingService.clearPressGaggle: Found thread:', thread.id)

      // Delete all messages in the Press Gaggle thread (all years)
      const { error: messagesError } = await supabase
        .from('messages')
        .delete()
        .eq('thread_id', thread.id)

      if (messagesError) {
        console.error('messagingService.clearPressGaggle: Error deleting messages:', messagesError)
        throw messagesError
      }

      console.log('messagingService.clearPressGaggle: Successfully cleared all press gaggle messages')
      return true
    } catch (error) {
      console.error('messagingService.clearPressGaggle: Error clearing press gaggle:', error)
      throw error
    }
  }

  // Get Press Gaggle thread
  async getPressGaggleThread(): Promise<MessageThread | null> {
    const { data, error } = await supabase
      .from('message_threads')
      .select('*')
      .eq('type', 'press_gaggle')
      .eq('title', 'Press Gaggle')
      .single()
    
    if (error) {
      console.error('Error fetching Press Gaggle thread:', error)
      return null
    }
    
    return data
  }

  // Get messages for a thread with sender information
  async getMessagesForThread(threadId: string, eventYear?: number): Promise<Message[]> {
    let query = supabase
      .from('messages')
      .select(`
        *,
        sender:users!sender_id (
          id,
          name,
          role,
          outlet
        )
      `)
      .eq('thread_id', threadId)
    if (eventYear) query = query.eq('event_year', eventYear)
    const { data, error } = await query.order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching messages:', error)
      return []
    }

    return data || []
  }

  // Send a message
  async sendMessage(threadId: string, senderId: number | string, content: string, isUrgent = false, eventYear?: number): Promise<Message | null> {
    const insertData: any = {
      thread_id: threadId,
      sender_id: senderId,
      content: content.trim(),
      is_urgent: isUrgent
    }
    if (eventYear) insertData.event_year = eventYear
    const { data, error } = await supabase
      .from('messages')
      .insert(insertData)
      .select(`
        *,
        sender:users!sender_id (
          id,
          name,
          role,
          outlet
        )
      `)
      .single()
    
    if (error) {
      console.error('Error sending message:', error)
      return null
    }
    
    return data
  }

  // Update last read timestamp for user in thread
  async markThreadAsRead(threadId: string, userId: number | string): Promise<boolean> {
    const { error } = await supabase
      .from('message_participants')
      .update({ last_read_at: new Date().toISOString() })
      .eq('thread_id', threadId)
      .eq('user_id', userId)
    
    if (error) {
      console.error('Error marking thread as read:', error)
      return false
    }
    
    return true
  }

  // Get unread status for a user (boolean only, faster)
  async getUnreadStatus(userId: number | string, eventYear?: number): Promise<UnreadStatus> {
    // Get user's threads and their last read timestamps
    const { data: participants, error: participantsError } = await supabase
      .from('message_participants')
      .select(`
        thread_id, 
        last_read_at,
        message_threads!inner (
          id,
          type,
          title,
          is_active
        )
      `)
      .eq('user_id', userId)
      .eq('is_active', true)
      .eq('message_threads.is_active', true)
    
    if (participantsError) {
      console.error('Error fetching participants:', participantsError)
      return {}
    }
    
    const unreadStatus: UnreadStatus = {}
    
    // For each thread, check if there are any unread messages
    for (const participant of participants || []) {
      let msgQuery = supabase
        .from('messages')
        .select('id')
        .eq('thread_id', participant.thread_id)
        .gt('created_at', participant.last_read_at)
        .neq('sender_id', userId)
      if (eventYear) msgQuery = msgQuery.eq('event_year', eventYear)
      const { data, error: messageError } = await msgQuery.limit(1)
      
      if (!messageError && data && data.length > 0) {
        unreadStatus[participant.thread_id] = true
      } else {
        unreadStatus[participant.thread_id] = false
      }
    }
    
    return unreadStatus
  }

  // Get Press Gaggle thread ID
  async getPressGaggleThreadId(): Promise<string | null> {
    const { data, error } = await supabase
      .from('message_threads')
      .select('id')
      .eq('type', 'press_gaggle')
      .eq('title', 'Press Gaggle')
      .single()
    
    if (error) {
      console.error('Error fetching Press Gaggle thread ID:', error)
      return null
    }
    
    return data?.id || null
  }

  // Subscribe to real-time messages for a thread
  subscribeToThread(threadId: string, onMessage: (message: Message) => void) {
    const subscription = supabase
      .channel(`messages:${threadId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `thread_id=eq.${threadId}`
        },
        async (payload) => {
          // Fetch the complete message with sender info
          const { data, error } = await supabase
            .from('messages')
            .select(`
              *,
              sender:users!sender_id (
                id,
                name,
                role,
                outlet
              )
            `)
            .eq('id', payload.new.id)
            .single()
          
          if (!error && data) {
            onMessage(data)
          }
        }
      )
      .subscribe()
    
    return subscription
  }

  // Subscribe to thread creation/updates for a user
  subscribeToThreadUpdates(userId: number | string, onThreadUpdate: (thread: MessageThread) => void) {
    const subscription = supabase
      .channel('thread-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_threads',
          filter: `type=eq.group_thread`
        },
        async (payload) => {
          // Check if user is participant in this new thread
          const { data: isParticipant } = await supabase
            .from('message_participants')
            .select('id')
            .eq('thread_id', payload.new.id)
            .eq('user_id', userId)
            .eq('is_active', true)
            .single()
          
          if (isParticipant) {
            onThreadUpdate(payload.new as MessageThread)
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'message_threads',
          filter: `type=eq.group_thread`
        },
        async (payload) => {
          // Handle thread deletions (is_active = false)
          if (!payload.new.is_active) {
            // This will be handled by the component to remove from list
            onThreadUpdate(payload.new as MessageThread)
          }
        }
      )
      .subscribe()
    
    return subscription
  }

  // Unsubscribe from real-time updates
  unsubscribe(subscription: any) {
    supabase.removeChannel(subscription)
  }
}

// Export singleton instance
export const messagingService = new MessagingService()